import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { verlofApi } from '../../../lib/verlofApi';

export const verlofAfsluitenKeys = {
  all: ['hr', 'verlof-afsluiten'] as const,
  maand: (maand: string) => ['hr', 'verlof-afsluiten', maand] as const,
};

/**
 * Story 4.2: het maandoverzicht van de HR-pagina *Verlof afsluiten*.
 * `staleTime` 30 s, gelijk aan `useApprovalQueue` — dit is een lijst die door
 * andermans acties verandert (een bevestiging, een geslaagde doorzetting).
 */
export function useVerlofAfsluiten(maand: string) {
  return useQuery({
    queryKey: verlofAfsluitenKeys.maand(maand),
    queryFn: () => verlofApi.getVerlofAfsluiten(maand),
    staleTime: 1000 * 30,
  });
}

/**
 * De drie acties op deze pagina roepen bestaande eindpunten aan (story 2.2,
 * 2.3 en 3.3) en maken er geen nieuwe. Ze delen één ding: na afloop moet de
 * lijst kloppen zonder dat HR de pagina herlaadt.
 */
function useAfsluitActie<TArgs, TResult>(actie: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: actie,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: verlofAfsluitenKeys.all });
    },
  });
}

/** Blok ①: de doorzetting opnieuw proberen (bestaand eindpunt, story 3.3). */
export function useVerlofAfsluitenRetrySync() {
  return useAfsluitActie((id: number) => verlofApi.retrySync(id));
}

/** Blok ③: het kenmerk voor één regel terugzoeken (bestaand eindpunt, story 2.2). */
export function useVerlofAfsluitenLinkKenmerk() {
  return useAfsluitActie((id: number) => verlofApi.linkKenmerk(id));
}

/** De koppelactie over de hele historie (bestaand eindpunt, story 2.3). */
export function useVerlofKoppelactie() {
  return useAfsluitActie(() => verlofApi.linkAlleKenmerken());
}
